import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  CircularProgress,
  Alert,
} from "@mui/material";
import dayjs from "dayjs";
import { formatLocalDateOnly } from "../shared utils/dateFormatters";
import { CUTOFF_DATE } from "../../constants";
import { getStayBookings, getStudent, getStudentPayments } from "./api/agentsApi";
import { generateStudentInvoicePdf } from "../AllTabs/StudentTabs/studentInvoicePdf.js";

const money = (n) => `£${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? formatLocalDateOnly(new Date(d)) : "");

// Per-stay invoice for one student. Produces the SAME branded PDF as the student
// Invoice tab (shared generateStudentInvoicePdf), scoped to this stay's bookings.
// Gross vs net only changes the amounts (net reduces commissionable lines by the
// snapshot rate) — the invoice never names commission.
export default function InvoiceDialog({ open, onClose, agent, stay }) {
  const [bookings, setBookings] = useState([]);
  const [student, setStudent] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("gross"); // gross | net

  useEffect(() => {
    if (!open || !stay?.studentId || !stay?.stayId) return;
    setMode("gross");
    setLoading(true);
    setError(null);
    Promise.all([
      getStayBookings(stay.studentId, stay.stayId),
      getStudent(stay.studentId).catch(() => null),
      getStudentPayments(stay.studentId).catch(() => []),
    ])
      .then(([bk, st, pay]) => {
        setBookings(Array.isArray(bk) ? bk : []);
        setStudent(st || null);
        // Match the Invoice tab: only payments on/after the cutoff.
        const filtered = (Array.isArray(pay) ? pay : []).filter(
          (p) => new Date(p.paymentdate) >= new Date(CUTOFF_DATE)
        );
        setPayments(filtered);
      })
      .catch((e) => {
        setError(e);
        setBookings([]);
      })
      .finally(() => setLoading(false));
  }, [open, stay]);

  const rate = Number(stay?.commissionRate) || 0;
  const factor = (100 - rate) / 100;
  const gross = mode === "gross";

  // Net reduces commissionable lines by the snapshot rate; gross is untouched.
  const amountFor = useMemo(
    () => (b) => (!gross && b.commissionable ? b.actualCharge * factor : b.actualCharge),
    [gross, factor]
  );

  const total = useMemo(
    () => bookings.reduce((acc, b) => acc + amountFor(b), 0),
    [bookings, amountFor]
  );

  const downloadPdf = () => {
    generateStudentInvoicePdf({
      invoiceDate: dayjs(),
      student: student || {
        id: stay?.studentId,
        // Fall back to the agent-stay's display name if the student fetch failed.
        studentName: stay?.studentName || "",
        studentSurname: "",
      },
      selectedStay: stay, // carries arrivalDate / departureDate
      bookings,
      payments,
      gross,
      factor,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Invoice — {stay?.studentName} ({stay?.arrivalDate} – {stay?.departureDate})
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_, v) => v && setMode(v)}
            >
              <ToggleButton value="gross">Gross</ToggleButton>
              <ToggleButton value="net">Net</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Download produces the full branded invoice for this stay — identical in format to the
              student Invoice tab.
            </Typography>
          </Stack>

          {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading bookings…</Typography>
            </Stack>
          ) : bookings.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No bookings on this stay.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map((b) => (
                  <TableRow key={b.bookingId}>
                    <TableCell>{b.productName}</TableCell>
                    <TableCell>{fmtDate(b.startDate)}</TableCell>
                    <TableCell>{fmtDate(b.endDate)}</TableCell>
                    <TableCell align="right">{money(amountFor(b))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {money(total)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={downloadPdf} disabled={bookings.length === 0}>
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}
